import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rankBetween } from '@mas-flow/domain';
import type { Item } from '@mas-flow/types';
import * as itemsApi from '../api/items';
import type { RankInput } from '../api/items';

export const ITEMS_KEY = ['items'] as const;
export const itemKey = (id: string) => ['item', id] as const;

export function useItems(includeArchived = false) {
  return useQuery({
    queryKey: includeArchived ? ['items', 'all'] : ITEMS_KEY,
    queryFn: () => itemsApi.list(includeArchived),
    staleTime: 5 * 60_000,
  });
}

export function useItem(id: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: id ? itemKey(id) : ['item', '_none'],
    queryFn: () => itemsApi.getById(id!),
    enabled: id !== null,
    initialData: () => {
      if (!id) return undefined;
      const list = qc.getQueryData<Item[]>(ITEMS_KEY);
      return list?.find((i) => i.id === id);
    },
    staleTime: 30_000,
  });
}

export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Item>) => itemsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}

export function usePatchItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch, etag }: { id: string; patch: Partial<Item>; etag: string }) =>
      itemsApi.patch(id, patch, etag),
    onSuccess: (item) => {
      qc.setQueryData(itemKey(item.id), item);
      qc.setQueryData<Item[]>(ITEMS_KEY, (prev) => prev?.map((x) => (x.id === item.id ? item : x)));
    },
    onError: (_e, vars) => {
      qc.invalidateQueries({ queryKey: itemKey(vars.id) });
      qc.invalidateQueries({ queryKey: ITEMS_KEY });
    },
  });
}

export function useRankItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RankInput) => itemsApi.rank(input),
    // Optimistic: move the card in the cache immediately so it doesn't snap
    // back to its old column for the duration of the API round trip.
    // Deliberately synchronous (cancelQueries is not awaited): the kanban
    // clears its local drag state right after mutate(), and the cache must
    // already hold the move by then or the card flickers back for a frame.
    onMutate: (input) => {
      void qc.cancelQueries({ queryKey: ITEMS_KEY });
      const previous = qc.getQueryData<Item[]>(ITEMS_KEY);
      qc.setQueryData<Item[]>(ITEMS_KEY, (prev) =>
        prev?.map((x) => {
          if (x.id !== input.itemId) return x;
          let columnRank = x.columnRank;
          try {
            columnRank = rankBetween(input.beforeRank, input.afterRank);
          } catch {
            // No representable midpoint (e.g. exhausted alphabet) — keep the
            // old rank; the server result will correct the position.
          }
          return { ...x, decision: input.targetColumn, columnRank };
        }),
      );
      return { previous };
    },
    onSuccess: (item) => {
      qc.setQueryData<Item[]>(ITEMS_KEY, (prev) => prev?.map((x) => (x.id === item.id ? item : x)));
      qc.setQueryData(itemKey(item.id), item);
    },
    onError: (_e, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData<Item[]>(ITEMS_KEY, ctx.previous);
      qc.invalidateQueries({ queryKey: ITEMS_KEY });
    },
  });
}

export function useAnchorItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isAnchor, anchorWhy, etag }: { id: string; isAnchor: boolean; anchorWhy: string | null; etag: string }) =>
      itemsApi.setAnchor(id, isAnchor, anchorWhy, etag),
    onSuccess: (item) => {
      qc.setQueryData(itemKey(item.id), item);
      qc.setQueryData<Item[]>(ITEMS_KEY, (prev) => prev?.map((x) => (x.id === item.id ? item : x)));
    },
  });
}

export function useArchiveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archive, etag }: { id: string; archive: boolean; etag: string }) =>
      itemsApi.setArchived(id, archive, etag),
    onSuccess: (item) => {
      qc.setQueryData(itemKey(item.id), item);
      qc.setQueryData<Item[]>(ITEMS_KEY, (prev) => prev?.map((x) => (x.id === item.id ? item : x)));
    },
  });
}
